import React from 'react';
import PageHero from '@/components/PageHero';
import FadeInUp from '@/components/FadeInUp';
import { BUSINESS } from '@/lib/site';
import { MapPinIcon, PhoneIcon, FaxIcon, MailIcon, ClockIcon, ArrowRight } from '@/components/icons';
import { Link } from 'react-router-dom';

export default function Location() {
  return (
    <>
      <PageHero
        eyebrow="Find Us"
        title="East Tamaki, Auckland."
        subtitle="Our training centre is conveniently located in the heart of Auckland’s industrial district. Easy on/off-ramp access and bus stops at our door."
        image="/generated/training-facility.png"
        breadcrumbs={[{label:'Home', to:'/'}, {label:'Location'}]}
      />

      <section className="nft-container py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <FadeInUp>
              <div className="surface-card overflow-hidden" data-testid="location-map">
                <div className="aspect-[4/3] sm:aspect-[16/10] w-full">
                  <iframe
                    title="NFT location map"
                    src={BUSINESS.mapEmbed}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </div>
            </FadeInUp>
          </div>

          <div className="lg:col-span-5 space-y-6" data-testid="location-contact-details">
            <FadeInUp>
              <div className="surface-card p-6">
                <div className="label-eyebrow text-[#FC4E2C] mb-2">Address</div>
                <h2 className="font-display font-extrabold uppercase text-2xl text-[#060D9C] leading-tight">
                  {BUSINESS.name}
                </h2>
                <div className="mt-3 flex items-start gap-2 text-sm text-[#060D9C]/85">
                  <MapPinIcon width={18} height={18} className="shrink-0 mt-0.5 text-[#FC4E2C]" />
                  <div>
                    {BUSINESS.address.line1}<br />
                    {BUSINESS.address.line2}<br />
                    {BUSINESS.address.city} {BUSINESS.address.postcode}<br />
                    {BUSINESS.address.country}
                  </div>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.05}>
              <div className="surface-card p-6">
                <div className="label-eyebrow text-[#FC4E2C] mb-3">Contact</div>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-center gap-2">
                    <PhoneIcon width={18} height={18} className="text-[#FC4E2C]" />
                    <a href={BUSINESS.phoneHref} className="font-bold hover:text-[#FC4E2C]">{BUSINESS.phone}</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaxIcon width={18} height={18} className="text-[#FC4E2C]" />
                    <span>{BUSINESS.fax}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <PhoneIcon width={18} height={18} className="text-[#FC4E2C]" />
                    <a href={BUSINESS.mobileHref} className="font-bold hover:text-[#FC4E2C]">{BUSINESS.mobile}</a>
                    <span className="text-xs text-[#060D9C]/60">(urgent / text)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <MailIcon width={18} height={18} className="text-[#FC4E2C]" />
                    <a href={BUSINESS.emailHref} className="font-bold hover:text-[#FC4E2C] break-all">{BUSINESS.email}</a>
                  </li>
                  <li className="flex items-center gap-2">
                    <ClockIcon width={18} height={18} className="text-[#FC4E2C]" />
                    <span>{BUSINESS.hours}</span>
                  </li>
                </ul>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.1}>
              <div className="callout-steel">
                <div className="label-eyebrow text-[#FC4E2C] mb-2">Getting Here — Bus Routes</div>
                <p className="text-sm text-[#060D9C]/85">
                  For the latest bus timetables visit{' '}
                  <a href="https://at.govt.nz" target="_blank" rel="noopener noreferrer" className="underline text-[#060D9C] hover:text-[#FC4E2C]">AT.govt.nz</a>.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {BUSINESS.busRoutes.map(r => (
                    <span key={r} className="inline-flex items-center px-3 py-1 rounded-full bg-[#060D9C] text-white font-mono-cert text-sm font-bold">{r}</span>
                  ))}
                </div>
                <p className="mt-3 text-xs text-[#060D9C]/60 italic">Note: Verify current routes with Auckland Transport.</p>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.15}>
              <Link to="/booking" className="btn-primary-orange w-full justify-center">
                Book a Course <ArrowRight width={16} height={16} />
              </Link>
            </FadeInUp>
          </div>
        </div>
      </section>
    </>
  );
}
